export type ClientSearchCriterion = {
  name: string;
  value: unknown;
  controltype?: string;
};

export type ClientSortCriterion = {
  field: string;
  value: "asc" | "desc";
};

const getRowValue = (row: Record<string, unknown>, field: string): string => {
  const direct = row[field];
  if (direct !== undefined && direct !== null) {
    return String(direct);
  }
  const lower = row[field.toLowerCase()];
  if (lower !== undefined && lower !== null) {
    return String(lower);
  }
  return "";
};

const rowMatchesDateSearch = (rowValue: string, searchValue: string): boolean => {
  const rv = rowValue.trim().toLowerCase();
  const sv = searchValue.trim().toLowerCase();
  if (!sv) return true;
  if (!rv) return false;
  if (rv.includes(sv)) return true;

  const ymd = rv.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const dmy = `${ymd[3]}-${ymd[2]}-${ymd[1]}`;
    if (dmy.includes(sv) || sv.includes(dmy)) return true;
  }

  const dmy = rv.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
  if (dmy) {
    const normalized = `${dmy[1].padStart(2, "0")}-${dmy[2].padStart(2, "0")}-${dmy[3]}`;
    if (normalized.includes(sv) || sv.includes(normalized)) return true;
  }

  return false;
};

const rowMatchesCriterion = (
  row: Record<string, unknown>,
  criterion: ClientSearchCriterion
): boolean => {
  const { name, value, controltype } = criterion;
  if (
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  ) {
    return true;
  }

  const cell = getRowValue(row, name);
  const control = String(controltype || "").toLowerCase();

  if (Array.isArray(value)) {
    return value.some(
      (v) => cell.toLowerCase() === String(v).toLowerCase()
    );
  }

  const searchStr = String(value).trim();
  if (!searchStr) return true;

  if (control === "datetime" || control === "date") {
    return rowMatchesDateSearch(cell, searchStr);
  }

  if (typeof row[name] === "boolean") {
    return cell.toLowerCase() === searchStr.toLowerCase();
  }

  return cell.toLowerCase().includes(searchStr.toLowerCase());
};

/** Filter rows using SUMMARY_LIST search criteria (custom GET endpoints without server search). */
export function applyClientSearchFilters<T extends Record<string, unknown>>(
  rows: T[],
  search?: ClientSearchCriterion[]
): T[] {
  if (!search?.length) return rows;
  return rows.filter((row) =>
    search.every((criterion) => rowMatchesCriterion(row, criterion))
  );
}

export function applyClientSort<T extends Record<string, unknown>>(
  rows: T[],
  sort?: ClientSortCriterion[]
): T[] {
  if (!sort?.length) return rows;
  const { field, value: direction } = sort[0];
  if (!field) return rows;

  const copy = [...rows];
  copy.sort((a, b) => {
    const aValue = a[field];
    const bValue = b[field];
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return direction === "asc" ? -1 : 1;
    if (bValue == null) return direction === "asc" ? 1 : -1;
    if (aValue < bValue) return direction === "asc" ? -1 : 1;
    if (aValue > bValue) return direction === "asc" ? 1 : -1;
    return 0;
  });
  return copy;
}

/** Paginate after client filter/sort (for custom GET list endpoints). */
export function paginateClientRows<T>(
  rows: T[],
  skip = 0,
  limit = 20
): { data: T[]; totalcount: number } {
  const totalcount = rows.length;
  const safeSkip = Math.max(0, skip);
  const safeLimit = Math.max(1, limit);
  return {
    data: rows.slice(safeSkip, safeSkip + safeLimit),
    totalcount,
  };
}
